import { Installments } from 'components/Cart/OrderSummary/types';
import type { TFunction } from 'i18next';
import type {
  IBundle,
  InstallmentPlanPricing,
  IMoney,
  IProduct,
  IProductPricing,
  ISoftwarePlan,
} from 'services/dcs/bundle';
import { getObjectKeyLength } from './Object';
import { SUBSCRIPTION_STATE } from 'hooks/useSubscriptionsState';
import { calculateSubscriptionMonthlyPrice } from 'components/Pricing/utils/PricingUtil';
import { BooleanFilter } from './TypeUtils';

/**
 * Removes all keys from an object that contain undefined or null values
 * @param object The object to clean
 * @returns {object} The object with all keys that contain undefined or null values removed
 */
const cleanObject = (object: Record<string, any>): object => {
  const updatedObject = {};
  Object.keys(object).forEach(key => {
    const currentValue = object[key];
    const isObject = currentValue && typeof currentValue === 'object';
    const isDefinedValue = !isObject && currentValue !== undefined && currentValue !== null;
    if (isObject) {
      const nestedObj = cleanObject(currentValue);
      if (getObjectKeyLength(nestedObj)) {
        updatedObject[key] = nestedObj;
      }
    } else if (isDefinedValue) {
      updatedObject[key] = currentValue;
    }
  });
  return updatedObject;
};

const getInstallmentPlanMap = (productPricing: IProductPricing[]) => {
  if (!productPricing) return {};

  return productPricing.reduce((map, pricing) => {
    if (pricing.installmentPlan.code !== Installments.P) {
      map[pricing.installmentPlan.code] = pricing.price;
    }
    return map;
  }, {});
};

const getPricingObject = (t: TFunction, pricingObject: any[], msrp: IMoney | null) => {
  const pricing = pricingObject?.find(price => price?.installmentPlan?.code === Installments.P);
  if (!pricing) return {};

  const basePrice = pricing.price;
  const installmentPlans: InstallmentPlanPricing = getInstallmentPlanMap(pricingObject);

  return {
    msrp: msrp ? t('pricing-template-strings.msrp', { price: msrp.value }) : null,
    total: basePrice ? t('pricing-template-strings.price', { price: basePrice.value }) : null,
    installmentPlans: {
      sl36: installmentPlans.SL36?.value
        ? t('pricing-template-strings.installment-plan-price', { price: installmentPlans.SL36.value })
        : null,
      sl48: installmentPlans.SL48?.value
        ? t('pricing-template-strings.installment-plan-price', { price: installmentPlans.SL48.value })
        : null,
    },
  };
};

const transformBundleForTemplate = (
  t: TFunction,
  subscriptionState: SUBSCRIPTION_STATE,
  bundle?: IBundle,
) => {
  if (!bundle) return;

  const { productPricing, msrp } = bundle;
  const bundlePricingObject = getPricingObject(t, productPricing, msrp);

  const { transactionRates, monthlyPrice, additionalDevicePrice } = bundle.softwarePlanPricing ?? {};
  const { keyedInRate, inPersonRate } = transactionRates ?? {};
  const monthlyAdditionalPrice = monthlyPrice?.value;
  const isMonthlyPriceAvailable = BooleanFilter(monthlyAdditionalPrice) || monthlyAdditionalPrice === 0;

  const installmentPlans: InstallmentPlanPricing = getInstallmentPlanMap(productPricing);
  const subscriptionMonthlyPrice = isMonthlyPriceAvailable
    ? calculateSubscriptionMonthlyPrice(subscriptionState, installmentPlans, monthlyAdditionalPrice)
    : 0;

  return cleanObject({
    pricing: {
      softwarePlan: {
        monthlyPrice: isMonthlyPriceAvailable
          ? t('pricing-template-strings.price', { price: monthlyAdditionalPrice })
          : null,
        additionalDevicePrice: additionalDevicePrice?.value
          ? t('pricing-template-strings.price', {
              price: additionalDevicePrice.value,
            })
          : null,
        transactionRates: {
          keyedInRate: {
            percentage: keyedInRate?.percentage
              ? t('pricing-template-strings.percentage', { percentage: keyedInRate.percentage })
              : null,
            fixedAmount: keyedInRate?.fixedAmount?.value
              ? t('pricing-template-strings.transaction-rates.fixed-amount', {
                  fixedAmount: keyedInRate.fixedAmount.value,
                })
              : null,
          },
          inPersonRate: {
            percentage: inPersonRate?.percentage
              ? t('pricing-template-strings.percentage', { percentage: inPersonRate.percentage })
              : null,
            fixedAmount: inPersonRate?.fixedAmount?.value
              ? t('pricing-template-strings.transaction-rates.fixed-amount', {
                  fixedAmount: inPersonRate.fixedAmount.value,
                })
              : null,
          },
        },
      },
      subscriptions: {
        monthlyPrice: BooleanFilter(subscriptionMonthlyPrice)
          ? t('pricing-template-strings.subscription-monthly-price', {
              price: subscriptionMonthlyPrice,
            })
          : null,
        monthlyPriceAndString: BooleanFilter(subscriptionMonthlyPrice)
          ? t('pricing-template-strings.subscription-monthly-price-with-monthly-string', {
              price: subscriptionMonthlyPrice,
            })
          : null,
      },
      ...bundlePricingObject,
    },
  });
};

const transformProductForTemplate = (t: TFunction, product?: IProduct) => {
  if (!product) return;

  const { msrp } = product;
  const productPricingObject = getPricingObject(t, product.pricing, msrp);

  return cleanObject({
    pricing: {
      ...productPricingObject,
    },
  });
};

const transformSoftwarePlanPricingForTemplate = (t: TFunction, softwarePlan?: ISoftwarePlan) => {
  if (!softwarePlan?.pricing) return;

  return cleanObject({
    pricing: {
      monthlyPrice: t('pricing-template-strings.price', { price: softwarePlan.pricing.monthlyPrice.value }),
      additionalDevicePrice: t('pricing-template-strings.price', {
        price: softwarePlan.pricing.additionalDevicePrice.value,
      }),
      transactionRates: {
        keyedInRate: {
          percentage: t('pricing-template-strings.percentage', {
            percentage: softwarePlan.pricing.transactionRates.keyedInRate.percentage,
          }),
          fixedAmount: t('pricing-template-strings.transaction-rates.fixed-amount', {
            fixedAmount: softwarePlan.pricing.transactionRates.keyedInRate.fixedAmount.value,
          }),
        },
        inPersonRate: {
          percentage: t('pricing-template-strings.percentage', {
            percentage: softwarePlan.pricing.transactionRates.inPersonRate.percentage,
          }),
          fixedAmount: t('pricing-template-strings.transaction-rates.fixed-amount', {
            fixedAmount: softwarePlan.pricing.transactionRates.inPersonRate.fixedAmount.value,
          }),
        },
      },
    },
  });
};

export { transformBundleForTemplate, transformProductForTemplate, transformSoftwarePlanPricingForTemplate };
